import React from 'react';
import { motion } from 'framer-motion';

/**
 * Small shared building blocks used by both the home page and the pricing page,
 * so the two never drift apart visually.
 */

/**
 * Fades a block up as it scrolls into view, once. Wrapping this in one place
 * keeps the same easing and distance everywhere instead of a slightly different
 * hand-written motion.div on every section.
 */
export const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}> = ({ children, delay = 0, y = 24, className }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/** Centred section header. `invert` switches it for the dark ink sections. */
export const SectionHeading: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  sub?: React.ReactNode;
  invert?: boolean;
  align?: 'center' | 'left';
}> = ({ eyebrow, title, sub, invert = false, align = 'center' }) => (
  <div
    className={
      align === 'center'
        ? 'mx-auto max-w-3xl text-center'
        : 'max-w-3xl text-left'
    }
  >
    {eyebrow && (
      <Reveal>
        <span className={`eyebrow mb-4 ${invert ? 'text-accent-300' : 'text-primary-600'}`}>
          <span className={`h-1 w-1 rounded-full ${invert ? 'bg-accent-300' : 'bg-primary-500'}`} />
          {eyebrow}
        </span>
      </Reveal>
    )}
    <Reveal delay={0.05}>
      <h2 className={invert ? 'h-section-invert' : 'h-section'}>{title}</h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.1}>
        <p className={`mt-4 ${invert ? 'lede-invert' : 'lede'} ${align === 'center' ? 'mx-auto' : ''}`}>
          {sub}
        </p>
      </Reveal>
    )}
  </div>
);

/**
 * Counts up to a value the first time it enters the viewport.
 * Uses an IntersectionObserver rather than a scroll handler so it costs nothing
 * while off-screen, and it renders the final value immediately when the user
 * has asked for reduced motion.
 */
export const AnimatedCounter: React.FC<{
  value: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
}> = ({ value, duration = 1600, suffix = '', prefix = '' }) => {
  const [count, setCount] = React.useState(0);
  const hasRun = React.useRef(false);
  const ref = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setCount(value);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasRun.current) return;
          hasRun.current = true;

          let start: number | null = null;
          const step = (now: number) => {
            if (start === null) start = now;
            const progress = Math.min((now - start) / duration, 1);
            // Ease-out so the number decelerates into its final value.
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(value * eased));
            if (progress < 1) requestAnimationFrame(step);
            else setCount(value);
          };
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className="tabular">
      {prefix}
      {count}
      {suffix}
    </span>
  );
};
